import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconArrowLeft, IconCheck, IconPlus, IconTrash, IconUpload, IconLoader2, IconFile, IconVideo, IconLink, IconTrashX } from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const emptyLesson = () => ({
  title: "", duration_min: 8, type: "video", body: "",
  // Video principal (uno u otro)
  video_url: "", video_file: null,
  // Documentos PDF (hasta 2)
  documents: [],
  // Legacy fields kept for backwards-compat loads
  file_id: null, file_url: null, file_name: null,
});
const emptyModule = () => ({ title: "", lessons: [emptyLesson()] });

const MAX_DOCS_PER_LESSON = 2;
const VIDEO_ACCEPT = "video/*";
const PDF_ACCEPT = ".pdf,application/pdf";

export default function NuevoCurso() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const { courseId } = useParams(); // present in edit route
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(courseId);
  const initialTrack = searchParams.get("track") || "docente";
  const [form, setForm] = useState({
    title: "", subtitle: "", description: "",
    track: initialTrack, level: "intro", duration_min: 30, tags: "",
    is_capsule: false,
    price: 0, is_free: false, status: "published",
  });
  const [modules, setModules] = useState([emptyModule()]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [uploads, setUploads] = useState({});
  const [loadingInit, setLoadingInit] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/aula/courses/${courseId}`);
        setForm({
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          track: data.track || "docente",
          level: data.level || "intro",
          duration_min: data.duration_min || 30,
          tags: (data.tags || []).join(", "),
          is_capsule: !!data.is_capsule,
          price: Number(data.price || 0),
          is_free: !!data.is_free,
          status: data.status || "published",
        });
        const loadedModules = (data.modules || []).map((m) => ({
          title: m.title,
          lessons: (m.lessons || []).map((l) => {
            // Backwards-compat: map legacy fields (file_url + type) into video_file/documents.
            const documents = Array.isArray(l.documents) ? [...l.documents] : [];
            let video_file = l.video_file || null;
            const legacyType = l.type;
            const legacyFile = l.file_url ? { file_id: l.file_id, file_url: l.file_url, file_name: l.file_name } : null;
            if (legacyFile && !video_file && documents.length < MAX_DOCS_PER_LESSON) {
              if (legacyType === "video") video_file = legacyFile;
              else documents.push(legacyFile);
            }
            return {
              title: l.title || "",
              duration_min: l.duration_min || 8,
              type: l.type || "video",
              body: l.body || "",
              video_url: l.video_url || "",
              video_file,
              documents,
              file_id: null, file_url: null, file_name: null, // legacy cleared post-migration
            };
          }),
        }));
        setModules(loadedModules.length ? loadedModules : [emptyModule()]);
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoadingInit(false); }
    })();
  }, [courseId, isEdit]);

  // Auto-detect video duration client-side via HTMLVideoElement metadata
  const detectVideoDuration = (file) => new Promise((resolve) => {
    if (!file || !file.type.startsWith("video/")) return resolve(null);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      const seconds = video.duration || 0;
      URL.revokeObjectURL(video.src);
      resolve(seconds > 0 ? Math.max(1, Math.round(seconds / 60)) : null);
    };
    video.onerror = () => { URL.revokeObjectURL(video.src); resolve(null); };
    video.src = URL.createObjectURL(file);
  });

  const updateLesson = (mi, li, patch) => {
    setModules((prev) => prev.map((mm, i) => i === mi ? { ...mm, lessons: mm.lessons.map((ll, j) => j === li ? { ...ll, ...patch } : ll) } : mm));
  };

  const uploadVideo = async (mi, li, file) => {
    if (!file) return;
    const key = `${mi}-${li}-video`;
    setUploads((u) => ({ ...u, [key]: { loading: true, progress: 0, error: null } }));
    try {
      const detected = await detectVideoDuration(file);
      if (detected) updateLesson(mi, li, { duration_min: detected });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/aula/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploads((u) => ({ ...u, [key]: { ...u[key], progress: pct } }));
          }
        },
      });
      updateLesson(mi, li, {
        video_file: { file_id: data.id, file_url: data.url, file_name: data.original_filename, size_bytes: data.size_bytes },
        video_url: "",
      });
      setUploads((u) => ({ ...u, [key]: { loading: false, progress: 100, error: null } }));
    } catch (err) {
      setUploads((u) => ({ ...u, [key]: { loading: false, progress: 0, error: formatApiError(err) } }));
    }
  };

  const uploadDoc = async (mi, li, file) => {
    if (!file) return;
    const key = `${mi}-${li}-doc`;
    setUploads((u) => ({ ...u, [key]: { loading: true, progress: 0, error: null } }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/aula/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            setUploads((u) => ({ ...u, [key]: { ...u[key], progress: pct } }));
          }
        },
      });
      setModules((prev) => prev.map((mm, i) => i === mi ? {
        ...mm,
        lessons: mm.lessons.map((ll, j) => {
          if (j !== li) return ll;
          const documents = [...(ll.documents || [])];
          if (documents.length >= MAX_DOCS_PER_LESSON) return ll;
          documents.push({ file_id: data.id, file_url: data.url, file_name: data.original_filename, size_bytes: data.size_bytes });
          return { ...ll, documents };
        }),
      } : mm));
      setUploads((u) => ({ ...u, [key]: { loading: false, progress: 100, error: null } }));
    } catch (err) {
      setUploads((u) => ({ ...u, [key]: { loading: false, progress: 0, error: formatApiError(err) } }));
    }
  };

  const removeDoc = (mi, li, di) => {
    setModules((prev) => prev.map((mm, i) => i === mi ? {
      ...mm,
      lessons: mm.lessons.map((ll, j) => j === li ? { ...ll, documents: (ll.documents || []).filter((_, k) => k !== di) } : ll),
    } : mm));
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        ...form,
        duration_min: Number(form.duration_min),
        price: Number(form.price || 0),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        modules: modules
          .filter((m) => m.title)
          .map((m) => ({
            title: m.title,
            lessons: m.lessons.filter((l) => l.title).map((l) => {
              // Derive canonical `type` from what's attached (backwards-compat + FE serialization)
              const hasVideo = Boolean(l.video_url || l.video_file);
              const derivedType = hasVideo ? "video" : (l.documents && l.documents.length ? "reading" : (l.type || "video"));
              return {
                title: l.title,
                duration_min: Number(l.duration_min),
                type: derivedType,
                body: l.body || null,
                video_url: l.video_url || null,
                video_file: l.video_file || null,
                documents: (l.documents || []).slice(0, MAX_DOCS_PER_LESSON),
              };
            }),
          })),
      };
      if (isEdit) {
        const { data } = await api.patch(`/aula/courses/${courseId}`, payload);
        nav(`/app/aula/${data.id}`);
      } else {
        const { data } = await api.post("/aula/courses", payload);
        nav(`/app/aula/${data.id}`);
      }
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const removeCourse = async () => {
    if (!isEdit) return;
    if (!window.confirm(lang === "es" ? "¿Eliminar este curso permanentemente?" : "Delete this course permanently?")) return;
    setDeleting(true);
    try { await api.delete(`/aula/courses/${courseId}`); nav("/app/aula"); }
    catch (err) { setError(formatApiError(err)); }
    finally { setDeleting(false); }
  };

  if (loadingInit) return <div className="wrap-app max-w-[760px] py-10"><IconLoader2 className="animate-spin" /></div>;

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app/aula")} data-testid="nc-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Aula" : "Aula"}
        </button>
        <span className="t">{isEdit ? (lang === "es" ? "EDITAR CURSO" : "EDIT COURSE") : (lang === "es" ? "NUEVO CURSO" : "NEW COURSE")}</span>
      </div>
      <form className="wrap-app max-w-[760px] py-10" onSubmit={submit}>
        <p className="eyebrow">{isEdit ? (lang === "es" ? "Editar curso" : "Edit course") : (lang === "es" ? "Publicar curso" : "Publish course")}</p>
        <h1 className="font-display font-medium text-[26px] mt-1 mb-6">
          {isEdit ? form.title || "—" : (lang === "es" ? "Nuevo curso" : "New course")}
        </h1>

        <div className="field"><label>{lang === "es" ? "Título" : "Title"}</label><input className="input" required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="course-title" /></div>
        <div className="field"><label>{lang === "es" ? "Subtítulo" : "Subtitle"}</label><input className="input" value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></div>
        <div className="field"><label>{lang === "es" ? "Descripción" : "Description"}</label><textarea className="textarea" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="field"><label>{lang === "es" ? "Para" : "For"}</label>
            <select className="select-css" value={form.track} onChange={(e) => set("track", e.target.value)} data-testid="course-track">
              <option value="docente">Docente</option>
              <option value="sombra">Sombra</option>
              <option value="familia">Familia</option>
              <option value="escuela">Escuela</option>
              <option value="ambos">Ambos</option>
            </select>
          </div>
          <div className="field"><label>{lang === "es" ? "Nivel" : "Level"}</label>
            <select className="select-css" value={form.level} onChange={(e) => set("level", e.target.value)}>
              <option value="intro">Introducción</option><option value="intermedio">Intermedio</option><option value="avanzado">Avanzado</option><option value="capsula">Cápsula</option>
            </select>
          </div>
          <div className="field"><label>{lang === "es" ? "Minutos" : "Minutes"}</label><input type="number" min="5" className="input" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} /></div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="field"><label>{lang === "es" ? "Precio USD" : "Price USD"}</label>
            <input type="number" min="0" step="0.5" className="input" value={form.price} onChange={(e) => set("price", e.target.value)} disabled={form.is_free} data-testid="course-price" />
          </div>
          <label className="flex items-center gap-2 text-[13px] self-end pb-3">
            <input type="checkbox" checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} data-testid="course-is-free" />
            {lang === "es" ? "Gratuito" : "Free"}
          </label>
          <div className="field"><label>{lang === "es" ? "Estado" : "Status"}</label>
            <select className="select-css" value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="published">{lang === "es" ? "Publicado" : "Published"}</option>
              <option value="draft">Borrador</option>
            </select>
          </div>
        </div>
        <div className="field"><label>{lang === "es" ? "Tags (separados por coma)" : "Tags (comma-separated)"}</label><input className="input" placeholder="sueño, TDAH, regulación" value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="course-tags" /></div>
        <label className="flex items-center gap-2 text-[13px] mt-2">
          <input type="checkbox" checked={form.is_capsule} onChange={(e) => set("is_capsule", e.target.checked)} data-testid="course-is-capsule" />
          {lang === "es" ? "Es una cápsula" : "Is a capsule"}
        </label>

        <p className="lbl mt-8">{lang === "es" ? "Estructura" : "Structure"}</p>
        {modules.map((m, mi) => (
          <div key={mi} className="card mt-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="tag">{lang === "es" ? "Módulo" : "Module"} {mi + 1}</span>
              {modules.length > 1 && (
                <button type="button" className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => setModules(modules.filter((_, i) => i !== mi))}>
                  <IconTrash size={12} />
                </button>
              )}
            </div>
            <div className="field"><label>{lang === "es" ? "Título del módulo" : "Module title"}</label>
              <input className="input" value={m.title} onChange={(e) => setModules(modules.map((mm, i) => i === mi ? { ...mm, title: e.target.value } : mm))} />
            </div>
            {m.lessons.map((l, li) => {
              const vKey = `${mi}-${li}-video`;
              const dKey = `${mi}-${li}-doc`;
              const vUp = uploads[vKey] || {};
              const dUp = uploads[dKey] || {};
              const docs = l.documents || [];
              const hasVideo = Boolean(l.video_url || l.video_file);
              return (
                <div key={li} className="border-t border-line pt-3 mt-3" data-testid={`lesson-${mi}-${li}`}>
                  <div className="grid sm:grid-cols-[2fr_1fr_auto] gap-2 items-end">
                    <div className="field mb-0"><label>{lang === "es" ? "Lección" : "Lesson"}</label>
                      <input className="input" value={l.title} onChange={(e) => updateLesson(mi, li, { title: e.target.value })} data-testid={`lesson-title-${mi}-${li}`} />
                    </div>
                    <div className="field mb-0"><label>Min {l.video_file && <span className="text-oktext text-[10px]">(auto)</span>}</label>
                      <input type="number" className="input" value={l.duration_min} onChange={(e) => updateLesson(mi, li, { duration_min: e.target.value })} />
                    </div>
                    <button type="button" className="btn-ghost btn text-[11px] px-2 py-1" onClick={() => setModules(modules.map((mm, i) => i === mi ? { ...mm, lessons: mm.lessons.filter((_, j) => j !== li) } : mm))} aria-label={lang === "es" ? "Eliminar lección" : "Delete lesson"}>
                      <IconTrash size={12} />
                    </button>
                  </div>

                  {/* ---------- Video (URL externa o archivo) ---------- */}
                  <div className="mt-3 p-3 rounded-lg bg-teal-soft/40 border border-line/60">
                    <p className="lbl m-0 mb-2 flex items-center gap-1"><IconVideo size={12} /> {lang === "es" ? "Video (opcional · 1 por lección)" : "Video (optional · 1 per lesson)"}</p>
                    {l.video_file ? (
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-[12.5px]">
                          <IconVideo size={14} />
                          <span className="font-medium break-all">{l.video_file.file_name}</span>
                          <span className="text-tert">{lang === "es" ? "subido" : "uploaded"}</span>
                        </div>
                        <button type="button" className="btn-ghost btn text-[11px]" onClick={() => updateLesson(mi, li, { video_file: null })} data-testid={`lesson-video-remove-${mi}-${li}`}>
                          {lang === "es" ? "Quitar" : "Remove"}
                        </button>
                      </div>
                    ) : vUp.loading ? (
                      <div className="flex items-center gap-2 text-[12.5px]">
                        <IconLoader2 size={14} className="animate-spin" />
                        <span>{lang === "es" ? "Subiendo" : "Uploading"} {vUp.progress}%</span>
                        <div className="flex-1 h-1.5 bg-line rounded-full overflow-hidden">
                          <div className="h-full bg-teal-deep transition-all" style={{ width: `${vUp.progress || 0}%` }} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-2">
                          <label className="lbl m-0 mb-1 flex items-center gap-1 text-tert"><IconLink size={11} /> {lang === "es" ? "URL externa (YouTube, Vimeo, Google Drive, Dropbox, mp4)" : "External URL (YouTube, Vimeo, Google Drive, Dropbox, mp4)"}</label>
                          <input
                            type="url"
                            className="input"
                            placeholder="https://youtube.com/… · https://drive.google.com/file/d/… · https://cdn.tudominio.com/video.mp4"
                            value={l.video_url || ""}
                            onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })}
                            data-testid={`lesson-video-url-${mi}-${li}`}
                          />
                          <p className="text-tert text-[10.5px] mt-1 leading-snug">
                            {lang === "es"
                              ? "Google Drive muestra un botón \"abrir en Drive\" que no se puede ocultar 100%. Para videos privados, sube el .mp4 al almacenamiento interno (más protegido: sin descarga ni pop-out)."
                              : "Google Drive shows an \"open in Drive\" button that cannot be fully hidden. For private videos, upload the .mp4 to internal storage (fully protected: no download or pop-out)."}
                          </p>
                        </div>
                        {!l.video_url && (
                          <label className="cursor-pointer inline-flex items-center gap-2 text-[12.5px] text-teal-deep hover:text-teal">
                            <IconUpload size={14} />
                            <span>{lang === "es" ? "…o sube el video (mp4, mov · máx 500 MB)" : "…or upload video (mp4, mov · max 500 MB)"}</span>
                            <input
                              type="file"
                              accept={VIDEO_ACCEPT}
                              className="hidden"
                              onChange={(e) => e.target.files?.[0] && uploadVideo(mi, li, e.target.files[0])}
                              data-testid={`lesson-video-input-${mi}-${li}`}
                            />
                          </label>
                        )}
                      </>
                    )}
                    {vUp.error && <p className="text-errtext text-[12px] mt-2">{vUp.error}</p>}
                  </div>

                  {/* ---------- Documentos PDF (hasta 2) ---------- */}
                  <div className="mt-2 p-3 rounded-lg bg-white border border-line/60">
                    <p className="lbl m-0 mb-2 flex items-center gap-1"><IconFile size={12} /> {lang === "es" ? `Documentos PDF (opcional · máx ${MAX_DOCS_PER_LESSON})` : `PDF documents (optional · max ${MAX_DOCS_PER_LESSON})`}</p>
                    {docs.length > 0 && (
                      <ul className="space-y-1 mb-2" data-testid={`lesson-docs-${mi}-${li}`}>
                        {docs.map((d, di) => (
                          <li key={di} className="flex items-center justify-between gap-2 text-[12.5px]">
                            <div className="flex items-center gap-2 min-w-0">
                              <IconFile size={13} className="shrink-0" />
                              <span className="font-medium truncate">{d.file_name}</span>
                              <span className="text-tert shrink-0">{lang === "es" ? "subido" : "uploaded"}</span>
                            </div>
                            <button type="button" className="btn-ghost btn text-[11px] px-2 py-0.5" onClick={() => removeDoc(mi, li, di)} data-testid={`lesson-doc-remove-${mi}-${li}-${di}`}>
                              <IconTrash size={11} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {dUp.loading ? (
                      <div className="flex items-center gap-2 text-[12.5px]">
                        <IconLoader2 size={13} className="animate-spin" />
                        <span>{lang === "es" ? "Subiendo" : "Uploading"} {dUp.progress}%</span>
                        <div className="flex-1 h-1.5 bg-line rounded-full overflow-hidden">
                          <div className="h-full bg-teal-deep transition-all" style={{ width: `${dUp.progress || 0}%` }} />
                        </div>
                      </div>
                    ) : docs.length < MAX_DOCS_PER_LESSON ? (
                      <label className="cursor-pointer inline-flex items-center gap-2 text-[12.5px] text-teal-deep hover:text-teal">
                        <IconUpload size={13} />
                        <span>{lang === "es" ? `Agregar PDF (máx 25 MB · ${docs.length}/${MAX_DOCS_PER_LESSON})` : `Add PDF (max 25 MB · ${docs.length}/${MAX_DOCS_PER_LESSON})`}</span>
                        <input
                          type="file"
                          accept={PDF_ACCEPT}
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDoc(mi, li, f); e.target.value = ""; }}
                          data-testid={`lesson-doc-input-${mi}-${li}`}
                        />
                      </label>
                    ) : (
                      <p className="text-tert text-[11px]">{lang === "es" ? "Límite de 2 PDFs alcanzado." : "Reached 2-PDF limit."}</p>
                    )}
                    {dUp.error && <p className="text-errtext text-[12px] mt-2">{dUp.error}</p>}
                  </div>
                </div>
              );
            })}
            <button type="button" className="btn-subt mt-3" onClick={() => setModules(modules.map((mm, i) => i === mi ? { ...mm, lessons: [...mm.lessons, emptyLesson()] } : mm))}>
              <IconPlus size={12} /> {lang === "es" ? "Agregar lección" : "Add lesson"}
            </button>
          </div>
        ))}
        <button type="button" className="btn-subt mt-3" onClick={() => setModules([...modules, emptyModule()])} data-testid="course-add-module">
          <IconPlus size={12} /> {lang === "es" ? "Agregar módulo" : "Add module"}
        </button>

        {error && <p className="text-errtext text-[13px] mt-3">{error}</p>}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
          <button type="submit" className="btn" disabled={saving} data-testid="course-submit">
            <IconCheck size={13} /> {saving ? "..." : isEdit ? (lang === "es" ? "Guardar cambios" : "Save changes") : (lang === "es" ? "Publicar curso" : "Publish course")}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={removeCourse}
              disabled={deleting}
              className="btn-ghost btn text-[12px] text-errtext"
              data-testid="course-delete"
            >
              <IconTrashX size={13} /> {deleting ? "..." : (lang === "es" ? "Eliminar curso" : "Delete course")}
            </button>
          )}
        </div>
      </form>
    </>
  );
}
